import type { ComponentProps } from 'react'
import { CheckIcon, XIcon } from 'lucide-react'
import { useLingui } from '@lingui/react/macro'
import { FieldInfo, hasFieldError, useFieldContext, useFormReadOnly, useIsFieldOptional } from './shared'
import { Input } from '../../ui/input'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../ui/tooltip'
import { FieldLabelRow } from './field-label-row'
import { useHydrated } from '@/hooks/use-hydrated'
import { normalizeHex, prefersDarkText } from '@/lib/accent-theme'
import {
  contrastRatio,
  findCompliantColor,
  parseHexColor,
  type ContrastTextColor,
  type Rgb,
} from '@/lib/color-contrast'
import { cn } from '@/lib/utils'
import { ButtonPrimitive } from '@/components/ui/button-primitive'

// WCAG text-contrast thresholds. AA is the legal floor in the EU under the European Accessibility Act
// (Directive 2019/882, in force 28 Jun 2025). https://eur-lex.europa.eu/eli/dir/2019/882/oj
const WCAG_LEVELS = [
  { label: 'AA', threshold: 4.5, severity: 'required' },
  { label: 'AAA', threshold: 7, severity: 'enhanced' },
] as const

export default function ColorField({
  label,
  className,
  optional,
  contrastTextColor,
  ...props
}: ComponentProps<'input'> & { label: string; optional?: boolean; contrastTextColor?: ContrastTextColor }) {
  const field = useFieldContext<string>()
  const formReadOnly = useFormReadOnly()
  const isReadOnly = props.readOnly ?? formReadOnly
  // The swatch is a RAW `<input type="color">`, so the hydration gate `Input` applies to itself needs applying by
  // hand here — under full SSR a change before React attaches onChange is silently dropped.
  const hydrated = useHydrated()
  const isOptional = useIsFieldOptional(field.name, optional)
  const value = field.state.value ?? ''
  const displayValue = isReadOnly && !value ? '-' : value

  return (
    <div className={className}>
      <div className="grid gap-2">
        {label && (
          <FieldLabelRow
            label={label}
            htmlFor={field.name}
            hasError={field.state.meta.isTouched && !field.state.meta.isValid}
            optional={isOptional}
          />
        )}
        <div className="relative">
          {value && (
            <span
              aria-hidden
              className="border-input pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 rounded-full border"
              style={{ background: value }}
            />
          )}
          {!isReadOnly && (
            <input
              type="color"
              value={value}
              disabled={!hydrated}
              onBlur={field.handleBlur}
              onChange={(e) => field.handleChange(e.target.value)}
              className="absolute top-1/2 left-3 size-4 -translate-y-1/2 cursor-pointer opacity-0"
              tabIndex={-1}
            />
          )}
          <Input
            type="text"
            id={field.name}
            name={field.name}
            aria-invalid={hasFieldError(field.state.meta)}
            value={displayValue}
            onBlur={field.handleBlur}
            onChange={(e) => field.handleChange(e.target.value)}
            {...props}
            readOnly={isReadOnly}
            tabIndex={isReadOnly ? -1 : props.tabIndex}
            className={cn(
              value && 'pl-8',
              isReadOnly &&
                'border-transparent bg-transparent focus-visible:border-transparent focus-visible:ring-0 dark:bg-transparent',
              isReadOnly && !value && 'px-0',
            )}
          />
        </div>
        {!isReadOnly && (
          <ContrastHint color={value} textColor={contrastTextColor} onApply={(next) => field.handleChange(next)} />
        )}
      </div>
      <FieldInfo fieldState={field.state} />
    </div>
  )
}

/** Live WCAG-contrast hints for the picked color, against the text color the app paints on top of it. */
function ContrastHint(props: { color: string; textColor?: ContrastTextColor; onApply: (hex: string) => void }) {
  const { t } = useLingui()
  const hex = normalizeHex(props.color)
  const rgb = hex ? parseHexColor(hex) : null
  if (!hex || !rgb) return null

  // The text colour the app actually paints on the accent (accent-theme.prefersDarkText), so the hint is real.
  const effective: ContrastTextColor = props.textColor ?? (prefersDarkText(hex) ? 'black' : 'white')
  const textRgb = effective === 'white' ? { r: 255, g: 255, b: 255 } : { r: 0, g: 0, b: 0 }
  const ratio = contrastRatio(rgb, textRgb)

  return (
    <TooltipProvider>
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <Tooltip>
          <TooltipTrigger
            render={
              <ButtonPrimitive
                type="button"
                className="inline-flex cursor-help items-center rounded px-1.5 py-0.5 font-medium tabular-nums"
                style={{ backgroundColor: hex, color: effective }}
              >
                {t`Contrast`} {ratio.toFixed(2)}:1
              </ButtonPrimitive>
            }
          />
          <TooltipContent className="max-w-xs">
            <p className="text-xs leading-snug">
              {effective === 'white'
                ? t`How readable white text is on this color. WCAG asks for at least 4.5:1 (AA) — the EU's European Accessibility Act requires AA — or 7:1 (AAA).`
                : t`How readable dark text is on this color. WCAG asks for at least 4.5:1 (AA) — the EU's European Accessibility Act requires AA — or 7:1 (AAA).`}
            </p>
          </TooltipContent>
        </Tooltip>
        {WCAG_LEVELS.map((level) => (
          <ContrastCheck
            key={level.label}
            label={level.label}
            threshold={level.threshold}
            required={level.severity === 'required'}
            ratio={ratio}
            brand={rgb}
            textColor={effective}
            onApply={props.onApply}
          />
        ))}
      </div>
    </TooltipProvider>
  )
}

function ContrastCheck(props: {
  label: string
  threshold: number
  required: boolean
  ratio: number
  brand: Rgb
  textColor: ContrastTextColor
  onApply: (hex: string) => void
}) {
  const { t } = useLingui()
  // Plain locals: a lingui placeholder is named after its variable, and the catalog keys on `{label}`/`{threshold}`.
  const label = props.label
  const threshold = props.threshold
  const passes = props.ratio >= props.threshold
  const reached = props.ratio.toFixed(2) // extracted: lingui messages take a plain variable, not an expression
  const suggested = passes ? null : findCompliantColor(props.brand, props.textColor, props.threshold)
  const Icon = passes ? CheckIcon : XIcon

  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <span
            className={cn(
              'inline-flex items-center gap-1 rounded px-1.5 py-0.5 tabular-nums',
              passes
                ? 'bg-emerald-500/10 text-emerald-700 dark:bg-emerald-400/10 dark:text-emerald-400'
                : props.required
                  ? 'bg-destructive/10 text-destructive'
                  : 'bg-muted text-muted-foreground',
            )}
          >
            <span className="font-medium">{props.label}</span>
            <span>{props.threshold}</span>
            <Icon className="size-3" />
          </span>
        }
      />
      <TooltipContent className="max-w-64">
        <div className="flex flex-col gap-1.5 text-xs leading-snug">
          <p>{t`WCAG ${label} needs at least ${threshold}:1.`}</p>
          <p>
            {passes ? t`Your color reaches ${reached}:1 — it passes.` : t`Your color reaches ${reached}:1 — it fails.`}
          </p>
          {suggested && (
            <div className="flex flex-col gap-1 pt-1">
              <p className="text-muted-foreground">{t`A closer shade that meets ${label}:`}</p>
              <ButtonPrimitive
                type="button"
                onClick={() => props.onApply(suggested)}
                className="border-border bg-background hover:bg-accent flex w-full items-center gap-2 rounded border px-2 py-1.5 text-left transition-colors"
              >
                <span
                  aria-hidden
                  className="border-border inline-block size-4 shrink-0 rounded border"
                  style={{ backgroundColor: suggested }}
                />
                <span className="tabular-nums">{suggested}</span>
                <span className="text-muted-foreground ml-auto">{t`Apply`}</span>
              </ButtonPrimitive>
            </div>
          )}
          {!suggested && !passes && (
            <p className="text-muted-foreground">
              {props.textColor === 'white'
                ? t`No darker shade of this hue can reach ${threshold}:1.`
                : t`No lighter shade of this hue can reach ${threshold}:1.`}
            </p>
          )}
        </div>
      </TooltipContent>
    </Tooltip>
  )
}
